import { ICONS } from "app/assets/icons/icons";
import { Button, ButtonTheme } from "app/components/Button/Button";
import { Input } from "app/components/Input/Input";
import { List, ListItem } from "app/components/List/List";
import { Modal as ModalElement, ModalActions, ModalContent } from "app/components/Modal/Modal";
import { TextArea } from "app/components/TextArea/TextArea";
import { k, l } from "app/i18n";
import { pluralize } from "app/i18n/i18n.util";
import { Collection } from "app/models";
import { Trademark } from "app/models/trademark.model";
import { useAppDispatch, useAppSelector } from "app/redux";
import { collectionDetailRoute } from "app/routes";
import { handleError } from "app/util/error-handler";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { addTrademarksToCollection, createCollection } from "../../../redux/slices/collection.slice";
import styles from "./AddTrademarkToCollectionModal.module.scss";

export enum AddTrademarkToCollectionModalView {
  ADD = "add",
  NEW = "new",
}

type AddTrademarkToCollectionModalProps = {
  open: boolean;
  onClose: () => void;
  onCreateNewCollection?: (collectionId: string) => void;
  trademarks?: Trademark[];
  defaultView?: AddTrademarkToCollectionModalView;
};

export const AddTrademarkToCollectionModal = ({
  open,
  onClose,
  trademarks = [],
  defaultView = AddTrademarkToCollectionModalView.ADD,
  onCreateNewCollection = () => null,
}: AddTrademarkToCollectionModalProps) => {
  const items = useAppSelector((state) => state.collection.items);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [view, setView] = useState<AddTrademarkToCollectionModalView>(defaultView);
  const [newCollectionName, setNewCollectionName] = useState<string>("");
  const [newCollectionNote, setNewCollectionNote] = useState<string>("");

  useEffect(() => {
    if (trademarks.length === 0) {
      setView(AddTrademarkToCollectionModalView.NEW);
    } else {
      setView(AddTrademarkToCollectionModalView.ADD);
    }
  }, [trademarks]);

  const goToCollection = (id: string) => {
    navigate(collectionDetailRoute(id));
  };

  const handleAddNewCollection = async () => {
    try {
      const res = await dispatch(
        createCollection({
          name: newCollectionName,
          note: newCollectionNote,
          trademarks: trademarks.map((trademark) => ({
            trademarkId: trademark.id,
            trademarkOffice: trademark.office,
          })),
        }),
      ).unwrap();

      if (!res) {
        return;
      }

      const collectionId = res.uid;

      const withMarks = trademarks?.length ? ` mit ${pluralize("trademarks", trademarks?.length)}` : "";
      toast.success(`Sammlung ${newCollectionName}${withMarks} erstellt`, {
        onClick: () => goToCollection(collectionId),
      });
      onCreateNewCollection(collectionId);
      onClose();
    } catch (error) {
      handleError("Sammlung konnte nicht erstellt werden.", error);
    }
  };

  const handleShowView = (v: AddTrademarkToCollectionModalView) => () => {
    setView(v);
  };

  const addTrademarkToCollection = (collection: Collection) => async () => {
    if (!trademarks) {
      return;
    }
    try {
      await dispatch(
        addTrademarksToCollection({
          id: collection.uid,
          trademarks: trademarks.map(({ id, office }) => ({
            trademarkId: id,
            trademarkOffice: office,
          })),
        }),
      ).unwrap();
      if (trademarks.length > 1) {
        toast.success(`${pluralize("trademarks", trademarks?.length)} Marken zu ${collection.name} hinzugefügt.`, {
          onClick: () => goToCollection(collection.uid),
        });
      } else {
        toast.success(
          `${trademarks.map((trademark) => trademark.name).join(", ")} zu ${collection.name} hinzugefügt.`,
          {
            onClick: () => goToCollection(collection.uid),
          },
        );
      }
      onClose();
    } catch (error) {
      handleError(`Marke konnte nicht zur Sammlung hinzugefügt werden.`, error);
    }
  };

  const listItems: ListItem[] = items
    .map((l) => ({
      id: l.uid,
      title: l.name,
      active: false,
      subtitle: `${pluralize("trademarks", l.trademarks?.length || 0)}`,
      onClick: addTrademarkToCollection(l),
    }))
    .sort((a, b) => a.title.localeCompare(b.title));

  const newConfig = {
    title: l(k.CREATE_NEW_COLLECTION),
    subtitle: l(k.CREATE_NEW_COLLECTION_SUBTITLE),
    content: (
      <>
        {trademarks.length > 0 ? (
          <Button
            theme={ButtonTheme.LINK}
            onClick={handleShowView(AddTrademarkToCollectionModalView.ADD)}
            className={styles.seeAll}
          >
            <ICONS.CHEVRON_LEFT /> Alle Sammlungen
          </Button>
        ) : null}
        <Input placeholder={`Name der Sammlung`} onTextChange={setNewCollectionName} onEnter={handleAddNewCollection} />
        <TextArea placeholder="Notiz" value={newCollectionNote} onTextChange={setNewCollectionNote} />
      </>
    ),
    actions: (
      <Button onClickPromise={handleAddNewCollection} disabled={!newCollectionName}>
        Sammlung erstellen
      </Button>
    ),
  };
  const listConfig = {
    title: l(k.ADD_TO_COLLECTION),
    subtitle: `Fügen Sie ${
      trademarks.length === 1 ? `${trademarks[0]?.name || "diese Marke"}` : `${trademarks.length} Marken`
    } zu einer Sammlung hinzu.`,
    content: <List items={listItems} />,
    actions: (
      <>
        <Button theme={ButtonTheme.TRANSPARENT} onClick={handleShowView(AddTrademarkToCollectionModalView.NEW)}>
          {l(k.CREATE_NEW_COLLECTION)}
        </Button>
        <Button theme={ButtonTheme.OUTLINE} onClick={onClose}>
          Schließen
        </Button>
      </>
    ),
  };

  const config = view === AddTrademarkToCollectionModalView.NEW ? newConfig : listConfig;

  return (
    <ModalElement open={open} onClose={onClose} title={config.title} subtitle={config.subtitle}>
      <ModalContent>{config.content}</ModalContent>
      <ModalActions>{config.actions}</ModalActions>
    </ModalElement>
  );
};
